import { Spinner, theme } from 'aimo-ui';
import AccountDefault from 'components/account/AccountDefault';
import Notifications from 'components/account/notifications/Notifications';
import VehicleAdd from 'components/account/vehicle/VehicleAdd';
import BGView from 'components/common/BGView';
import HelpButton from 'components/common/HelpButton';
import {
  HistoryFilterButtonNoTypes,
  HistoryFilterButtonWithTypes,
} from 'components/common/HistoryFilterButton';
import { RetryPaymentPolling } from 'hooks/useRetryPaymentResult';
import StackHeader, {
  HeaderType,
  StackHeaderCustomProps,
} from 'navigation/StackHeader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Benefits from 'screens/account/Benefits';
import ChargingHistoryDetailsScreen from 'screens/account/ChargingHistoryDetailsScreen';
import Chatbot from 'screens/account/Chatbot';
import ContactInfo from 'screens/account/ContactInfo';
import ContactUs from 'screens/account/ContactUs';
import CorporateAccount from 'screens/account/CorporateAccount';
import EditVehicle from 'screens/account/EditVehicle';
import MyPaymentMethods from 'screens/account/MyPaymentMethods';
import MyVehicles from 'screens/account/MyVehicles';
import ParkingHistoryDetailsScreen from 'screens/account/ParkingHistoryDetailsScreen';
import ParkingHistoryScreen from 'screens/account/ParkingHistoryScreen';
import PermitHistoryDetailsScreen from 'screens/account/PermitHistoryDetailsScreen';
import PrepaidHistoryScreen from 'screens/account/PrepaidHistoryScreen';
import PrepaidTicketHistoryDetailsScreen from 'screens/account/PrepaidTicketHistoryDetailsScreen';
import SheetWrapper, { WebWrapper } from 'screens/account/SheetWrapper';
import Subscriptions from 'screens/account/Subscriptions';
import TagsAndMifareCard from 'screens/account/TagsAndMifareCard';
import Tutorials from 'screens/account/Tutorials';
import ViewTerms from 'screens/account/ViewTerms';
import { confirmCreateSalesOrderData } from 'states/common';
import { useUiStore } from 'stores/uiStore';
import { useVehicleStore } from 'stores/vehicleStore';
import { ScreenContainer, StyledWrapper } from 'styles/ContainerStyles';
import { AccountStackParamList } from 'types/navigation';
import { logCustomEvent } from 'utils/analytics';
import { isWeb } from 'utils/deviceUtils';
import { createStackNavigator } from 'utils/navigationHelpers';
import { transitionOptions } from 'utils/navigationUtils';
import { StackHeaderComponentTitleSmall } from './navigationComponents';

type RoundBackgroundViewProps = {
  page: React.FC;
  loading?: boolean;
  mb?: number;
};

const AccountStack = createStackNavigator<AccountStackParamList>();

const CustomHelpButton = () => {
  return <HelpButton testID="help-button" />;
};

const DefaultHeader = (props: StackHeaderCustomProps) => (
  <CustomHeaderComponent {...props} headerType="default" />
);

const HistoryHeader = (props: StackHeaderCustomProps) => (
  <CustomHeaderComponent {...props} headerType="history" />
);

const MainHeader = (props: StackHeaderCustomProps) => (
  <CustomHeaderComponent
    {...props}
    headerType="main"
    headerBackIconVisible={false}
    titleSize="large"
  />
);

const CustomHeaderComponent = (props: StackHeaderCustomProps) => (
  <StackHeader
    {...props}
    useInsetForTop
    background={background(props.headerType)}
  />
);

const background = (headerType: HeaderType = 'default') => {
  if (headerType === 'history') {
    return isWeb ? theme.colors.white : theme.colors.gray100;
  }
  return isWeb ? theme.colors.white : theme.colors.lightBg.pink;
};

const RoundBackgroundView: React.FC<RoundBackgroundViewProps> = ({
  page: Page,
  loading,
  mb,
}) => {
  const usedTheme = useUiStore((state) => state.accountTheme);
  return (
    <ScreenContainer background={usedTheme} mb={mb}>
      {loading ? (
        <StyledWrapper center>
          <Spinner />
        </StyledWrapper>
      ) : (
        <BGView noMargins paddings={{ top: 20 }}>
          <Page />
        </BGView>
      )}
    </ScreenContainer>
  );
};

const getScreen = <P,>(
  Component: React.FC,
  loading?: boolean,
  mb?: number,
  isSheet?: boolean
) => {
  if (isWeb) {
    return (props: P) => <WebWrapper page={Component} {...props} />;
  } else {
    if (isSheet) {
      return (props: P) => (
        <SheetWrapper page={Component} mb={mb} loading={loading} {...props} />
      );
    } else {
      return (props: P) => (
        <RoundBackgroundView
          page={Component}
          mb={mb}
          loading={loading}
          {...props}
        />
      );
    }
  }
};

const ParkingHistoryScreenWithRetryPolling: React.FC = (props) => (
  <RetryPaymentPolling>
    <ParkingHistoryScreen {...props} />
  </RetryPaymentPolling>
);

const AccountNavigator: React.FC = () => {
  const { t } = useTranslation();
  const { vehicleData, resetVehicleStore } = useVehicleStore((state) => state);

  return (
    <AccountStack.Navigator
      screenOptions={{
        ...transitionOptions(),
        headerTransparent: isWeb,
        headerShown: true,
        cardStyle: {
          backgroundColor: isWeb
            ? theme.colors.white
            : theme.colors.lightBg.pink,
        },
        header: DefaultHeader,
      }}>
      <AccountStack.Screen
        name="accountMain"
        component={AccountDefault}
        options={{
          title: t('account.signedInWelcome'),
          header: MainHeader,
          headerRight: CustomHelpButton,
        }}
      />
      <AccountStack.Screen
        name="contactUs"
        component={ContactUs}
        options={{
          title: t('account.contactUs.title'),
        }}
      />
      <AccountStack.Screen
        name="tutorials"
        component={Tutorials}
        options={{
          title: t('account.tutorials.title'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
      />
      <AccountStack.Screen
        name="benefits"
        component={Benefits}
        options={{
          title: t('account.benefits'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.pink);
          },
        }}
      />
      <AccountStack.Screen
        name="myVehicles"
        component={MyVehicles}
        options={{
          title: t('account.myVehicles'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.pink);
          },
        }}
      />
      <AccountStack.Screen
        name="myPaymentMethods"
        component={MyPaymentMethods}
        options={{
          title: t('account.paymentMethods'),
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="addVehicleAccount"
        component={VehicleAdd}
        options={{
          title: t(`vehicle.addVehicle.type.${vehicleData?.appSettings?.type}`),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          beforeRemove: () => {
            resetVehicleStore();
          },
        }}
      />
      <AccountStack.Screen
        name="editVehicle"
        component={EditVehicle}
        options={{
          title: t('vehicle.editVehicle'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.pink);
          },
        }}
      />
      <AccountStack.Screen
        name="parkingHistory"
        component={getScreen(
          ParkingHistoryScreenWithRetryPolling,
          undefined,
          undefined,
          true
        )}
        options={{
          title: t('account.parkAndChargeHistory'),
          header: HistoryHeader,
          headerRight: HistoryFilterButtonWithTypes,
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.gray100);
          },
        }}
      />
      <AccountStack.Screen
        name="prepaidHistory"
        component={getScreen(PrepaidHistoryScreen, undefined, undefined, true)}
        options={{
          title: t('account.prepaid'),
          header: HistoryHeader,
          headerRight: HistoryFilterButtonNoTypes,
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.gray100);
          },
        }}
      />
      <AccountStack.Screen
        name="parkingHistoryDetails"
        component={ParkingHistoryDetailsScreen}
        options={{
          cardStyle: { backgroundColor: theme.colors.white },
          header: StackHeaderComponentTitleSmall,
          headerMode: isWeb ? 'float' : 'screen',
          headerBackButtonDisplayMode: isWeb ? 'minimal' : 'default',
          title: t('account.parkingDetails'),
        }}
      />

      <AccountStack.Screen
        name="permitHistoryDetails"
        component={PermitHistoryDetailsScreen}
        options={{
          cardStyle: {
            backgroundColor: isWeb ? theme.colors.white : theme.colors.gray100,
          },
          header: HistoryHeader,
        }}
      />
      <AccountStack.Screen
        name="prepaidHistoryDetails"
        component={PrepaidTicketHistoryDetailsScreen}
        options={{
          cardStyle: {
            backgroundColor: isWeb ? theme.colors.white : theme.colors.gray100,
          },
          header: HistoryHeader,
        }}
      />
      <AccountStack.Screen
        name="chargingHistoryDetails"
        component={ChargingHistoryDetailsScreen}
        options={{
          cardStyle: { backgroundColor: theme.colors.white },
          header: StackHeaderComponentTitleSmall,
          headerMode: isWeb ? 'float' : 'screen',
          headerBackButtonDisplayMode: isWeb ? 'minimal' : 'default',
          title: t('account.chargingDetails'),
        }}
      />
      <AccountStack.Screen
        name="contactInfo"
        component={ContactInfo}
        options={{
          title: t('account.contactInfo'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="companyPermit"
        component={CorporateAccount}
        options={{
          title: t('account.companyPermit'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="viewTerms"
        component={ViewTerms}
        options={{
          title: t('account.terms.privacyTermsAndConditions'),
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="notifications"
        component={Notifications}
        options={{
          title: t('account.notifications'),
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="mifareAndRfid"
        component={TagsAndMifareCard}
        options={{
          title: t('account.tagsAndCards.title'),
        }}
        listeners={{
          focus: () => {
            useUiStore.getState().setAccountTheme(theme.colors.lightBg.orange);
          },
        }}
      />
      <AccountStack.Screen
        name="chatbot"
        component={Chatbot}
        listeners={{
          focus: async () => await logCustomEvent('chatbot_opened'),
        }}
        options={{
          title: t('account.chatbot'),
        }}
      />
      <AccountStack.Screen
        name="aimoSubscription"
        component={Subscriptions}
        options={{
          title: t('account.aimoSubscription.title'),
          cardStyle: {
            backgroundColor: theme.colors.white,
          },
        }}
        listeners={{
          focus: () => {
            confirmCreateSalesOrderData(undefined);
          },
        }}
      />
    </AccountStack.Navigator>
  );
};

export default AccountNavigator;
